import React from 'react';
import {
  Textarea,
  Radio,
  Checkbox,
  Stack,
  Group,
  Text,
  Badge,
  Tooltip,
  Switch,
  UnstyledButton,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

type Question = {
  id: string;
  text: string;
  type: 'text' | 'scale' | 'select' | 'multi-select';
  required?: boolean;
  anonymous?: boolean;
  allowDeanonymize?: boolean;
  scaleMin?: number;
  scaleMax?: number;
  scaleLabels?: string[];
  options?: string[];
};

// Sentinel for "cannot assess" on scale questions — distinguishes a deliberate
// "I can't rate this" from "no answer yet" (undefined). Persisted as-is in
// form.answers; backend stores it via z.unknown().
export const CANNOT_ASSESS = '__cannot_assess__';

// Effective per-point labels for a scale: the questionnaire's own `scaleLabels`
// when set, else the standard 5-point expectation labels (`defaults`, supplied by
// the caller from i18n) for a 1..5 scale, else none. Single source so the editable
// widget and the read-only summary label points the same way.
export const resolveScaleLabels = (
  q: { scaleLabels?: string[]; scaleMin?: number; scaleMax?: number },
  defaults?: string[],
): string[] | undefined => {
  const configured = q.scaleLabels?.filter(Boolean);
  if (configured && configured.length) return q.scaleLabels;
  const min = q.scaleMin ?? 1;
  const max = q.scaleMax ?? 5;
  return max - min + 1 === 5 ? defaults : undefined;
};

interface Props {
  question: Question;
  value?: string | number | string[] | null;
  onChange: (value: string | number | string[] | null) => void;
  readOnly?: boolean;
  deanonymized?: boolean;
  onDeanonChange?: (v: boolean) => void;
}

interface ScaleInputProps {
  min: number;
  max: number;
  labels?: string[];
  value: number | null;
  onChange: (v: number) => void;
  readOnly?: boolean;
}

// Below→above expectations is a diverging axis, so the selected point carries a
// sentiment colour (warm = below, green = meets/exceeds). Meeting expectations is
// itself a success, so it reads green — not a neutral grey — with above/well-above
// meeting = green, above = teal, and well-above jumps to grape as a standout
// "exceptional" mark. Colour is spent only on the chosen row — every other row
// stays quiet. Non-5-point scales fall back to the accent colour.
const SENTIMENT_5 = ['red', 'orange', 'green', 'teal', 'grape'] as const;
const toneFor = (count: number, i: number): string => (count === 5 ? SENTIMENT_5[i] : 'teal');

// A scale answer stores 1..count; a real score is any positive number (0 / null /
// CANNOT_ASSESS mean "not scored"). Shared so the modal's required-comment rule
// and this widget agree on one definition.
export const isScaleScored = (value: unknown): boolean => typeof value === 'number' && value > 0;

// Whether a question has a usable answer. Single definition of "answered" for the
// scale sentinel + text/multi-select cases, shared by the form's progress/required
// logic so it can't drift from what the widgets store.
export const isAnswered = (q: { type?: string }, value: unknown): boolean => {
  if (value === undefined) return false;
  if (q.type === 'scale') return value === CANNOT_ASSESS || value === null || isScaleScored(value);
  if (q.type === 'multi-select') return Array.isArray(value) && value.length > 0;
  if (typeof value === 'string') return value.trim().length > 0;
  return value !== null;
};

interface ScaleOptionProps {
  selected: boolean;
  tone: string;
  label: string;
  // Shown in the indicator circle when the option is not selected (a number, a dash…).
  unselected: React.ReactNode;
  onClick: () => void;
  readOnly?: boolean;
  // Secondary option (e.g. "cannot assess"): muted label, no accent bar.
  subdued?: boolean;
}

// One row of the labelled scale. Both the numeric points and the "cannot assess"
// opt-out render through this, so they stay visually in lockstep.
const ScaleOption: React.FC<ScaleOptionProps> = ({
  selected,
  tone,
  label,
  unselected,
  onClick,
  readOnly,
  subdued,
}) => (
  <UnstyledButton
    onClick={() => !readOnly && onClick()}
    data-selected={selected || undefined}
    className="scale-option"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '8px 12px',
      borderRadius: 10,
      border: `1px solid ${selected ? `var(--mantine-color-${tone}-filled)` : 'var(--mantine-color-default-border)'}`,
      background: selected ? `var(--mantine-color-${tone}-light)` : 'transparent',
      cursor: readOnly ? 'default' : 'pointer',
      transition: 'background 120ms, border-color 120ms, box-shadow 120ms',
      boxShadow:
        selected && !subdued ? `inset 3px 0 0 var(--mantine-color-${tone}-filled)` : 'none',
      opacity: readOnly && !selected ? 0.45 : 1,
    }}
  >
    <span
      style={{
        width: 26,
        height: 26,
        flexShrink: 0,
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        background: selected ? `var(--mantine-color-${tone}-filled)` : 'transparent',
        color: selected ? 'var(--mantine-color-white)' : 'var(--mantine-color-dimmed)',
        border: selected ? 'none' : '1.5px solid var(--mantine-color-default-border)',
      }}
    >
      {selected ? <IconCheck size={15} /> : unselected}
    </span>
    <Text
      size="sm"
      c={subdued ? 'dimmed' : undefined}
      fw={selected ? 600 : 400}
      style={{ flex: 1, minWidth: 0 }}
    >
      {label}
    </Text>
  </UnstyledButton>
);

// Vertical labelled scale. The label is the hero — the score number sits in a
// small indicator, not repeated as an eyebrow. Replaces bare stars that gave no
// hint of what each point means.
const ScaleInput: React.FC<ScaleInputProps> = ({ min, max, labels, value, onChange, readOnly }) => {
  const count = Math.max(0, max - min + 1);
  return (
    <Stack gap={6}>
      {Array.from({ length: count }, (_, i) => {
        // Stored value stays 1..count for backward-compat with the old Rating widget.
        const optionValue = i + 1;
        return (
          <ScaleOption
            key={optionValue}
            selected={value === optionValue}
            tone={toneFor(count, i)}
            label={labels?.[i] || String(min + i)}
            unselected={min + i}
            onClick={() => onChange(optionValue)}
            readOnly={readOnly}
          />
        );
      })}
    </Stack>
  );
};

export const QuestionField: React.FC<Props> = ({
  question,
  value,
  onChange,
  readOnly,
  deanonymized,
  onDeanonChange,
}) => {
  const { t } = useTranslation();
  const canDeanonymize = question.anonymous === true && question.allowDeanonymize === true;
  const effectivelyAnonymous = question.anonymous === true && !(canDeanonymize && deanonymized);
  const label = (
    <Group gap={6} wrap="nowrap" align="flex-start">
      <Text fw={500} style={{ flex: 1 }}>
        {question.text}
        {question.required && (
          <Text component="span" c="red" inherit ml={4}>
            *
          </Text>
        )}
      </Text>
      {effectivelyAnonymous && (
        <Tooltip label={t('perfReview.anonymousTooltip')}>
          <Badge size="sm" variant="light" color="gray">
            {t('perfReview.anonymous')}
          </Badge>
        </Tooltip>
      )}
      {canDeanonymize && deanonymized && (
        <Badge size="sm" variant="light" color="teal">
          {t('perfReview.signed', 'С именем')}
        </Badge>
      )}
    </Group>
  );
  const deanonSwitch =
    canDeanonymize && !readOnly && onDeanonChange ? (
      <Switch
        size="xs"
        labelPosition="left"
        mb="sm"
        checked={!!deanonymized}
        onChange={(e) => onDeanonChange(e.currentTarget.checked)}
        label={
          <Text size="xs" c="dimmed">
            {t('perfReview.deanonHint', 'Раскрыть имя — этот ответ будет подписан в саммари')}
          </Text>
        }
      />
    ) : null;

  if (question.type === 'text') {
    return (
      <Stack gap={4}>
        {label}
        {deanonSwitch}
        <Textarea
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          autosize
          minRows={5}
          readOnly={readOnly}
        />
      </Stack>
    );
  }

  if (question.type === 'scale') {
    const min = question.scaleMin ?? 1;
    const max = question.scaleMax ?? 5;
    const cannotAssess = value === CANNOT_ASSESS || value === null;
    const defaults = t('perfReview.expectationScale', { returnObjects: true });
    const effectiveLabels = resolveScaleLabels(
      question,
      Array.isArray(defaults) ? (defaults as string[]) : undefined,
    );
    return (
      <Stack gap={10}>
        {label}
        <ScaleInput
          min={min}
          max={max}
          labels={effectiveLabels}
          value={typeof value === 'number' ? value : null}
          onChange={(v) => onChange(v)}
          readOnly={readOnly}
        />
        {/* Opt-out row — same ScaleOption shape, neutral grey to read as secondary. */}
        <ScaleOption
          selected={cannotAssess}
          tone="gray"
          label={t('perfReview.cannotAssess')}
          unselected="—"
          onClick={() => onChange(CANNOT_ASSESS as unknown as string)}
          readOnly={readOnly}
          subdued
        />
      </Stack>
    );
  }

  if (question.type === 'select') {
    return (
      <Stack gap={4}>
        {label}
        <Radio.Group value={(value as string) ?? ''} onChange={onChange}>
          <Stack gap={4}>
            {(question.options || []).map((opt) => (
              <Radio key={opt} value={opt} label={opt} disabled={readOnly} />
            ))}
          </Stack>
        </Radio.Group>
      </Stack>
    );
  }

  if (question.type === 'multi-select') {
    const arr = (value as string[]) ?? [];
    return (
      <Stack gap={4}>
        {label}
        <Checkbox.Group value={arr} onChange={onChange}>
          <Stack gap={4}>
            {(question.options || []).map((opt) => (
              <Checkbox key={opt} value={opt} label={opt} disabled={readOnly} />
            ))}
          </Stack>
        </Checkbox.Group>
      </Stack>
    );
  }

  return null;
};
